/**
 * Página do produto, preços e política de privacidade. Texto corrido e listas: em vez de um
 * catálogo de frases soltas, um objeto por língua com a mesma forma (a verificação de tipos
 * diz o que falta).
 */
import { formatNumber, type Locale } from '@nolens/i18n';
import {
  PRICES,
  SECONDS_SAVED_PER_STEP,
  TEAM_MAX_EDITORS,
  TEAM_MIN_EDITORS,
  TEAM_TRIAL_DAYS,
} from '@nolens/domain';

const MIN_PER_STEP = Math.round(SECONDS_SAVED_PER_STEP / 60);

/** Preço em euros na forma da língua: «€9» / «9 €». */
export const eur = (l: Locale, n: number) =>
  formatNumber(l, n, {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: Number.isInteger(n) ? 0 : 2,
  });

/**
 * Língua imposta pelo endereço de uma página pública (`/privacy` é sempre inglês,
 * `/privacidade` sempre português); null nas outras, que seguem a conta ou o browser.
 */
export function pathLocale(pathname: string): Locale | null {
  const p = pathname.replace(/\/+$/, '') || '/';
  if (p === '/testes') return 'pt-PT';
  for (const l of ['en', 'pt-PT'] as const)
    if ((Object.values(publicPaths(l)) as string[]).includes(p)) return l;
  return null;
}

/** Endereços das páginas públicas (os dois funcionam; os links seguem a língua). */
export const publicPaths = (l: Locale) =>
  l === 'pt-PT'
    ? {
        product: '/produto',
        pricing: '/precos',
        privacy: '/privacidade',
        terms: '/termos',
        refunds: '/reembolsos',
        beta: '/teste',
      }
    : {
        product: '/product',
        pricing: '/pricing',
        privacy: '/privacy',
        terms: '/terms',
        refunds: '/refunds',
        beta: '/beta',
      };

interface Item {
  title: string;
  text: string;
}

export interface LandingText {
  docTitle: string;
  home: string;
  nav: string;
  navHow: string;
  navModes: string;
  navUses: string;
  pricing: string;
  openApp: string;
  signIn: string;
  startFree: string;
  eyebrow: string;
  heroA: string;
  heroB: string;
  heroText: string;
  seePlans: string;
  heroTicks: string[];
  /** A gravação de exemplo, na abertura. */
  demoLabel: string;
  demoHost: string;
  demoRecording: string;
  demoSteps: [string, string];
  demoButton: string;
  demoFinish: string;
  factsLabel: string;
  facts: Item[];
  howTitle: string;
  howText: string;
  byHand: string;
  byHandSteps: string[];
  byHandResult: string;
  withUs: string;
  withUsSteps: string[];
  withUsResult: string;
  estimate: string;
  modesTitle: string;
  modesText: string;
  modes: { tab: string; title: string; points: string[]; note?: string }[];
  /** O guia de exemplo, no separador «Ler». */
  previewLabel: string;
  previewSteps: string[];
  previewBar: string;
  previewStepOf: string;
  previewButton: string;
  previewTip: string;
  /** A página com o guia interativo, no separador «Guiar». */
  guideStepOf: string;
  guideHint: string;
  guideField: string;
  /** A execução, no separador «Executar». */
  runTitle: string;
  runStepOf: string;
  runAsk: string;
  runConfirm: string;
  runPause: string;
  runButton: string;
  featuresTitle: string;
  spotlights: { tag: string; title: string; text: string }[];
  editorTools: string[];
  shotButton: string;
  pii: {
    customer: string;
    customerValue: string;
    taxId: string;
    iban: string;
    password: string;
    hidden: string;
  };
  exportFormats: string[];
  features: Item[];
  usesTitle: string;
  usesText: string;
  uses: { title: string; text: string; example: string }[];
  betaEyebrow: string;
  betaTitle: string;
  betaText: string;
  betaCta: string;
  privacyTitle: string;
  privacyText: string;
  security: string[];
  learnMore: string;
  privacyPolicy: string;
  plansTitle: string;
  plans: { n: string; p: string; t: string }[];
  comparePlans: string;
  faqTitle: string;
  faq: { q: string; a: string }[];
  closeTitle: string;
  closeText: string;
  demoSubject: string;
  askDemo: string;
  tagline: string;
  info: string;
  privacy: string;
  terms: string;
  refunds: string;
  contact: string;
  madeBy: string;
}

/** Ícones dos separadores «modes» e das funcionalidades pequenas, pela mesma ordem. */
export const landingIcons = {
  modes: ['📄', '▶', '⚡'],
  features: ['🗂️', '🔒', '👥', '💬', '✨', '⏱️'],
};

/** Passos de um procedimento típico, na comparação «à mão / com o Nolens Guides». */
const EXAMPLE_STEPS = 15;

const en = (): LandingText => ({
  docTitle: 'Nolens Guides — step-by-step guides, recorded while you work',
  home: 'Nolens Guides — home',
  nav: 'Product page',
  navHow: 'How it works',
  navModes: 'Ways to use',
  navUses: 'Use cases',
  pricing: 'Pricing',
  openApp: 'Open the app',
  signIn: 'Sign in',
  startFree: 'Start free',
  eyebrow: 'Chrome extension + web app',
  heroA: 'Do it once.',
  heroB: 'Explain it forever.',
  heroText:
    'Record the process in your browser and Nolens Guides writes the guide: every click becomes a step, with a screenshot zoomed in on the right spot and the element highlighted.',
  seePlans: 'See plans',
  heroTicks: ['Free for up to 10 guides', 'No card required', 'Data in the EU'],
  demoLabel: 'The extension recording a process',
  demoHost: 'billing.example.com',
  demoRecording: 'Recording · 2 steps',
  demoSteps: ['Open “Billing”', 'Click “New document”'],
  demoButton: '+ New document',
  demoFinish: 'Finish',
  factsLabel: 'In short',
  facts: [
    { title: 'Any web app', text: 'ERP, CRM, government portals, internal tools' },
    { title: 'Readers install nothing', text: 'The guide opens in the browser, also on a phone' },
    { title: 'Data in the EU', text: 'Frankfurt, isolated per company' },
    { title: 'English and Portuguese', text: 'In the app, the extension and the guides' },
  ],
  howTitle: `A ${EXAMPLE_STEPS}-step manual, by hand or recorded`,
  howText: 'The same procedure, two ways of documenting it.',
  byHand: 'By hand',
  byHandSteps: [
    'Do the process and take the screenshots',
    'Crop each image',
    'Draw arrows and boxes',
    'Hide personal data',
    'Write the captions and lay out the pages',
  ],
  byHandResult: `≈ ${EXAMPLE_STEPS * MIN_PER_STEP} min on screenshots and captions`,
  withUs: 'With Nolens Guides',
  withUsSteps: [
    'Press “Start recording” in the extension',
    'Do the task as usual',
    'Review the titles and press “Finish”',
  ],
  withUsResult: 'The guide is ready to share',
  estimate: `An estimate of about ${MIN_PER_STEP} minutes per step, not a measurement.`,
  modesTitle: 'One guide, three ways to use it',
  modesText:
    'The same guide onboards newcomers, helps people with questions and takes care of repetitive work.',
  modes: [
    {
      tab: 'Read',
      title: 'The step-by-step guide, wherever it’s needed',
      points: [
        'Screenshots with highlights, arrows and tips between steps',
        'On screen, as a PDF or as a presentation with a table of contents',
        'A public link or only for your workspace',
        'Readers don’t install anything',
      ],
    },
    {
      tab: 'Guide',
      title: 'Shows, on the page itself, where to click next',
      points: [
        'Opens the app and highlights each element',
        'Moves on when the person does the step',
        'Follows pages and tabs',
        'If it can’t find the element, it shows the screenshot',
      ],
    },
    {
      tab: 'Run',
      title: 'The extension does the steps for you',
      points: [
        'At the pace you choose, showing each step as it goes',
        'Buttons that usually change data ask for confirmation',
        'Field values: asked at the start, fixed text or a relative date',
        'You can pause at any time and do the rest yourself',
      ],
      note: 'In the Pro and Pro Team plans.',
    },
  ],
  previewLabel: 'Example of a guide',
  previewSteps: [
    'Open “Billing” in the menu',
    'Click “New document”',
    'Choose the customer',
    'Click “Issue”',
  ],
  previewBar: 'How to issue an invoice · 4 steps · saved about 8 min',
  previewStepOf: 'Step 2 of 4',
  previewButton: '+ New document',
  previewTip: '💡 Tip: if the button doesn’t show up, check that you have billing permissions.',
  guideStepOf: 'Step 3 of 4',
  guideHint: 'Choose the customer in the highlighted list.',
  guideField: 'Customer',
  runTitle: '⚡ Automatic run',
  runStepOf: 'Step 4 of 4 · Click “Issue”',
  runAsk: 'This step may change data. Confirm for the extension to do it — or do it yourself.',
  runConfirm: 'Do this step',
  runPause: 'Pause',
  runButton: 'Issue',
  featuresTitle: 'Everything a good manual needs',
  spotlights: [
    {
      tag: 'Screenshots',
      title: 'Zoomed in on the click, nothing to crop',
      text: 'One screenshot per step, with the click marked. Add highlights and arrows, blur or crop in the editor.',
    },
    {
      tag: 'Privacy',
      title: 'Personal data hidden before it leaves your computer',
      text: 'Emails, tax IDs, IBANs and cards are flagged and hidden. Passwords never make it into a screenshot. Banks and personal email stay out of recordings.',
    },
    {
      tag: 'Exports',
      title: 'With your company’s brand, in the format you need',
      text: 'Link, embed, PDF, HTML, Markdown or an editorial PowerPoint with a cover, a table of contents and one step per slide.',
    },
  ],
  editorTools: ['Highlight', 'Arrow', 'Blur', 'Crop'],
  shotButton: 'Issue',
  pii: {
    customer: 'Customer',
    customerValue: 'Example Company Ltd.',
    taxId: 'Tax ID',
    iban: 'IBAN',
    password: 'Password',
    hidden: 'Hidden',
  },
  exportFormats: ['Link', 'PDF', 'PowerPoint', 'HTML', 'Markdown', 'Embed'],
  features: [
    { title: 'Tabs and navigation', text: 'Records across tabs and pages without losing track.' },
    {
      title: 'Protected sites',
      text: 'Banks, tax portals and personal email stay out unless explicitly unlocked.',
    },
    { title: 'Teams and readers', text: 'Editors maintain guides; readers look them up.' },
    { title: 'Comments and views', text: 'Questions per step; views per day.' },
    { title: 'Optional AI', text: 'Suggested titles and description, only if you want.' },
    { title: 'Time saved', text: 'An estimate per month and per person.' },
  ],
  usesTitle: 'For anyone who explains the same process over and over',
  usesText: 'A few examples of what teams document first.',
  uses: [
    {
      title: 'Onboarding',
      text: 'Newcomers learn the tools without having to ask.',
      example: '“How to request time off in the portal”',
    },
    {
      title: 'Support',
      text: 'Answer with a link instead of a long email.',
      example: '“How to reset access”',
    },
    {
      title: 'Operations',
      text: 'The same procedure for the whole team.',
      example: '“How to issue an invoice”',
    },
    {
      title: 'Training',
      text: 'PowerPoint and PDF ready for the session.',
      example: '“Month-end close in the ERP”',
    },
  ],
  betaEyebrow: 'Early access',
  betaTitle: 'We’re just getting started. Try it with us.',
  betaText:
    'Use Nolens Guides for 2 to 3 weeks, free, on your everyday procedures. You’ll be talking to the people who build it: tell us what saves you time and what’s missing, and that’s what decides what we build next.',
  betaCta: 'Ask to try it',
  privacyTitle: 'Private by default',
  privacyText: 'A manual shows work screens. We treat them with that care.',
  security: [
    'While you record, nothing leaves your computer: the recording stays encrypted locally until you press “Finish”.',
    'Data in the European Union (Frankfurt) and screenshots under EU jurisdiction. We don’t sell data or use it for advertising.',
    'Each company’s data is isolated in the database itself, not just on screen.',
  ],
  learnMore: 'Learn more in the',
  privacyPolicy: 'Privacy policy',
  plansTitle: 'Simple plans',
  plans: [
    { n: 'Free', p: eur('en', 0), t: 'Up to 10 guides; record, edit, guide and PDF.' },
    {
      n: 'Pro',
      p: `${eur('en', PRICES.pro.monthly)}/month + VAT`,
      t: 'Unlimited guides, branding, editorial exports, public links, Run and AI.',
    },
    {
      n: 'Pro Team',
      p: `${eur('en', PRICES.team.monthly)}/editor/month + VAT`,
      t: `Everything in Pro, for ${TEAM_MIN_EDITORS} to ${TEAM_MAX_EDITORS} editors, with unlimited readers (free) and privacy rules for the whole team. ${TEAM_TRIAL_DAYS}-day trial with Pro.`,
    },
  ],
  comparePlans: 'Compare plans',
  faqTitle: 'Frequently asked questions',
  faq: [
    {
      q: 'What do I need to install?',
      a: 'Whoever records needs the Chrome extension. Readers don’t install anything: they open the guide in the browser, on a computer or a phone.',
    },
    {
      q: 'Which sites does it work on?',
      a: 'Any web application: ERP, CRM, government portals, internal tools. Sites with very sensitive data are protected and can only be recorded after you confirm.',
    },
    {
      q: 'Is “Run” safe?',
      a: 'The extension only does what’s in the guide, on the page the guide points to, and shows each step as it goes. You can stop whenever you want, and buttons that usually change data (save, send, pay, delete) ask for confirmation before they’re pressed.',
    },
    {
      q: 'How is the time saved estimated?',
      a: `We count about ${MIN_PER_STEP} minutes per documented step: the typical time it takes to grab a screenshot, crop it, highlight it and write the caption by hand. It’s an estimate, not a measurement.`,
    },
    {
      q: 'Can I try it for free?',
      a: `Yes. The Free plan includes up to 10 guides, no card required. With Pro, you can try Pro Team for ${TEAM_TRIAL_DAYS} days with everything included.`,
    },
  ],
  closeTitle: 'Record your next procedure',
  closeText:
    'Create an account, install the extension and do the task once. The guide is ready when you finish.',
  demoSubject: 'Nolens Guides demo',
  askDemo: 'Request a demo',
  tagline: 'Do it once. Explain it forever.',
  info: 'Information',
  privacy: 'Privacy',
  terms: 'Terms',
  refunds: 'Refunds',
  contact: 'Contact',
  madeBy: 'Built by',
});

const ptPT = (): LandingText => ({
  docTitle: 'Nolens Guides — guias passo a passo, gravados enquanto trabalha',
  home: 'Nolens Guides — página inicial',
  nav: 'Página do produto',
  navHow: 'Como funciona',
  navModes: 'Formas de usar',
  navUses: 'Para quem',
  pricing: 'Preços',
  openApp: 'Abrir a app',
  signIn: 'Entrar',
  startFree: 'Começar grátis',
  eyebrow: 'Extensão para o Chrome + app web',
  heroA: 'Faça uma vez.',
  heroB: 'Explique para sempre.',
  heroText:
    'Grave o processo no navegador e o Nolens Guides escreve o guia: cada clique vira um passo, com a captura, o zoom no sítio certo e o elemento destacado.',
  seePlans: 'Ver planos',
  heroTicks: ['Grátis até 10 guias', 'Sem cartão', 'Dados na UE'],
  demoLabel: 'A extensão a gravar um processo',
  demoHost: 'faturacao.exemplo.pt',
  demoRecording: 'A gravar · 2 passos',
  demoSteps: ['Abra «Faturação»', 'Clique em «Novo documento»'],
  demoButton: '+ Novo documento',
  demoFinish: 'Concluir',
  factsLabel: 'Em resumo',
  facts: [
    { title: 'Qualquer aplicação web', text: 'ERP, CRM, portais públicos, ferramentas internas' },
    { title: 'Quem lê não instala nada', text: 'O guia abre no navegador, também no telemóvel' },
    { title: 'Dados na UE', text: 'Frankfurt, isolados por empresa' },
    { title: 'Português e inglês', text: 'Na app, na extensão e nos guias' },
  ],
  howTitle: `Um manual de ${EXAMPLE_STEPS} passos, à mão ou gravado`,
  howText: 'O mesmo procedimento, duas formas de o documentar.',
  byHand: 'À mão',
  byHandSteps: [
    'Fazer o processo e tirar as capturas',
    'Recortar cada imagem',
    'Desenhar setas e caixas',
    'Tapar os dados pessoais',
    'Escrever as legendas e paginar',
  ],
  byHandResult: `≈ ${EXAMPLE_STEPS * MIN_PER_STEP} min em capturas e legendas`,
  withUs: 'Com o Nolens Guides',
  withUsSteps: [
    'Carregue em «Começar a gravar» na extensão',
    'Faça a tarefa como sempre',
    'Reveja os títulos e carregue em «Concluir»',
  ],
  withUsResult: 'O guia fica pronto a partilhar',
  estimate: `Estimativa de cerca de ${MIN_PER_STEP} minutos por passo, não uma medição.`,
  modesTitle: 'Um guia, três formas de o usar',
  modesText:
    'O mesmo guia serve para formar quem chega, apoiar quem tem dúvidas e despachar o que é repetitivo.',
  modes: [
    {
      tab: 'Ler',
      title: 'O guia passo a passo, onde for preciso',
      points: [
        'Capturas com destaques, setas e dicas entre os passos',
        'No ecrã, em PDF ou numa apresentação com sumário',
        'Ligação pública ou só para o seu espaço',
        'Quem lê não instala nada',
      ],
    },
    {
      tab: 'Guiar',
      title: 'Mostra, na própria página, onde clicar a seguir',
      points: [
        'Abre a aplicação e realça cada elemento',
        'Avança quando a pessoa faz o passo',
        'Segue páginas e separadores',
        'Se não encontrar o elemento, mostra a captura',
      ],
    },
    {
      tab: 'Executar',
      title: 'A extensão faz os passos por si',
      points: [
        'Ao ritmo que escolher, mostrando cada passo enquanto o faz',
        'Os botões que costumam alterar dados pedem confirmação',
        'Valores dos campos: pergunta no início, texto fixo ou data relativa',
        'Pode pausar a qualquer momento e fazer o resto você',
      ],
      note: 'Nos planos Pro e Pro Equipa.',
    },
  ],
  previewLabel: 'Exemplo de um guia',
  previewSteps: [
    'Abra «Faturação» no menu',
    'Clique em «Novo documento»',
    'Escolha o cliente',
    'Clique em «Emitir»',
  ],
  previewBar: 'Como emitir uma fatura · 4 passos · poupou cerca de 8 min',
  previewStepOf: 'Passo 2 de 4',
  previewButton: '+ Novo documento',
  previewTip: '💡 Dica: se o botão não aparecer, confirme que tem permissão de faturação.',
  guideStepOf: 'Passo 3 de 4',
  guideHint: 'Escolha o cliente na lista realçada.',
  guideField: 'Cliente',
  runTitle: '⚡ Execução automática',
  runStepOf: 'Passo 4 de 4 · Clique em «Emitir»',
  runAsk: 'Este passo pode alterar dados. Confirme para a extensão o fazer — ou faça-o você.',
  runConfirm: 'Fazer este passo',
  runPause: 'Pausar',
  runButton: 'Emitir',
  featuresTitle: 'Tudo o que um bom manual precisa',
  spotlights: [
    {
      tag: 'Capturas',
      title: 'Zoom no sítio do clique, sem recortar nada',
      text: 'Uma captura por passo, com o ponto do clique marcado. Acrescente destaques e setas, desfoque ou recorte no editor.',
    },
    {
      tag: 'Privacidade',
      title: 'Dados pessoais tapados antes de saírem do computador',
      text: 'E-mails, NIF, IBAN e cartões são assinalados e tapados. As palavras-passe nunca entram na captura. Bancos e e-mail pessoal ficam fora da gravação.',
    },
    {
      tag: 'Exportações',
      title: 'Com a marca da sua empresa, no formato que precisar',
      text: 'Ligação, embed, PDF, HTML, Markdown ou PowerPoint editorial, com capa, sumário e um passo por diapositivo.',
    },
  ],
  editorTools: ['Destaque', 'Seta', 'Desfocar', 'Recortar'],
  shotButton: 'Emitir',
  pii: {
    customer: 'Cliente',
    customerValue: 'Empresa Exemplo, Lda.',
    taxId: 'NIF',
    iban: 'IBAN',
    password: 'Palavra-passe',
    hidden: 'Tapado',
  },
  exportFormats: ['Ligação', 'PDF', 'PowerPoint', 'HTML', 'Markdown', 'Embed'],
  features: [
    {
      title: 'Separadores e navegação',
      text: 'Grava entre separadores e páginas, sem perder o fio.',
    },
    {
      title: 'Sites protegidos',
      text: 'Bancos, Finanças e e-mail pessoal ficam fora, salvo desbloqueio explícito.',
    },
    { title: 'Equipas e leitores', text: 'Editores mantêm os guias; leitores consultam.' },
    { title: 'Comentários e leituras', text: 'Dúvidas por passo; leituras por dia.' },
    { title: 'IA opcional', text: 'Títulos e descrição sugeridos, só se quiser.' },
    { title: 'Tempo poupado', text: 'Uma estimativa por mês e por pessoa.' },
  ],
  usesTitle: 'Para quem explica o mesmo processo muitas vezes',
  usesText: 'Alguns exemplos do que as equipas documentam primeiro.',
  uses: [
    {
      title: 'Integração',
      text: 'Quem chega aprende as ferramentas sem ter de perguntar.',
      example: '«Como pedir férias no portal»',
    },
    {
      title: 'Apoio ao cliente',
      text: 'Responda com uma ligação em vez de um e-mail comprido.',
      example: '«Como repor o acesso»',
    },
    {
      title: 'Operações',
      text: 'O mesmo procedimento para toda a equipa.',
      example: '«Como emitir uma fatura»',
    },
    {
      title: 'Formação',
      text: 'PowerPoint e PDF prontos para a sessão.',
      example: '«Fecho do mês no ERP»',
    },
  ],
  betaEyebrow: 'Acesso antecipado',
  betaTitle: 'Ainda estamos no início. Experimente connosco.',
  betaText:
    'Use o Nolens Guides durante 2 a 3 semanas, sem pagar, nos procedimentos do dia a dia. Do outro lado está quem o desenvolve: diga-nos o que lhe poupa tempo e o que falta, e é isso que decide o que fazemos a seguir.',
  betaCta: 'Quero experimentar',
  privacyTitle: 'Privado por omissão',
  privacyText: 'Um manual mostra ecrãs de trabalho. Tratamo-los com esse cuidado.',
  security: [
    'Enquanto grava, nada sai do seu computador: a gravação fica cifrada localmente até carregar em «Concluir».',
    'Dados na União Europeia (Frankfurt) e capturas com jurisdição UE. Não vendemos dados nem os usamos para publicidade.',
    'Os dados de cada empresa ficam isolados na própria base de dados, não apenas no ecrã.',
  ],
  learnMore: 'Saiba mais na',
  privacyPolicy: 'Política de privacidade',
  plansTitle: 'Planos simples',
  plans: [
    { n: 'Grátis', p: eur('pt-PT', 0), t: 'Até 10 guias, gravar, editar, guiar e PDF.' },
    {
      n: 'Pro',
      p: `${eur('pt-PT', PRICES.pro.monthly)}/mês + IVA`,
      t: 'Guias ilimitados, marca, exportações editoriais, ligações públicas, Executar e IA.',
    },
    {
      n: 'Pro Equipa',
      p: `${eur('pt-PT', PRICES.team.monthly)}/editor/mês + IVA`,
      t: `Tudo do Pro, de ${TEAM_MIN_EDITORS} a ${TEAM_MAX_EDITORS} editores, com leitores ilimitados (grátis) e regras de privacidade para toda a equipa. ${TEAM_TRIAL_DAYS} dias de teste com o Pro.`,
    },
  ],
  comparePlans: 'Comparar planos',
  faqTitle: 'Perguntas frequentes',
  faq: [
    {
      q: 'O que preciso de instalar?',
      a: 'Quem grava precisa da extensão para o Chrome. Quem só lê não instala nada: abre o guia no navegador, no computador ou no telemóvel.',
    },
    {
      q: 'Em que sites funciona?',
      a: 'Em qualquer aplicação web: ERP, CRM, portais públicos, ferramentas internas. Sites com dados muito sensíveis vêm protegidos e só se gravam depois de confirmar.',
    },
    {
      q: 'O «Executar» é seguro?',
      a: 'A extensão só faz o que está no guia, na página que o guia indica, e mostra cada passo enquanto o faz. Pode parar quando quiser, e os botões que costumam alterar dados (guardar, enviar, pagar, apagar) pedem confirmação antes de serem carregados.',
    },
    {
      q: 'Como é feita a estimativa de tempo poupado?',
      a: `Contamos cerca de ${MIN_PER_STEP} minutos por passo documentado: o tempo típico de tirar a captura, recortar, destacar e escrever a legenda à mão. É uma estimativa, não uma medição.`,
    },
    {
      q: 'Posso experimentar sem pagar?',
      a: `Sim. O plano Grátis tem até 10 guias, sem cartão. Com o Pro, pode experimentar o Pro Equipa durante ${TEAM_TRIAL_DAYS} dias, com tudo incluído.`,
    },
  ],
  closeTitle: 'Grave o próximo procedimento',
  closeText:
    'Crie a conta, instale a extensão e faça a tarefa uma vez. O guia fica pronto quando terminar.',
  demoSubject: 'Demonstração do Nolens Guides',
  askDemo: 'Pedir demonstração',
  tagline: 'Faça uma vez. Explique para sempre.',
  info: 'Informação',
  privacy: 'Privacidade',
  terms: 'Termos',
  refunds: 'Reembolsos',
  contact: 'Contacto',
  madeBy: 'Desenvolvido por',
});

const LANDING: Record<Locale, () => LandingText> = { en, 'pt-PT': ptPT };
export const landingText = (l: Locale): LandingText => LANDING[l]();
